/**
 * MasterEditModal.jsx — 예약 마스터 수정 모달 (Phase 3-B, 2026-04-30)
 *
 * Props:
 *   - master: 현재 마스터 데이터 (prefill)
 *   - onClose: 닫기 콜백
 *   - onSaved: 저장 성공 시 콜백 (parent 가 reload)
 *
 * API: PUT /api/admin/reservations/:code
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../api/apiClient';
import ExpandableTextarea from '../../../components/ExpandableTextarea';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import { stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalHeaderSub, stdModalCloseBtn } from '../adminlayout/designStandard';

const inputStyle = { padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg)', color: 'var(--text)', width: '100%', boxSizing: 'border-box', fontSize: 12 };
const labelStyle = { fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 };

const STATUS_OPTS = [{ value: 'confirmed', label: '확정' }, { value: 'cancelled', label: '취소' }, { value: 'completed', label: '완료' }];

function numberValue(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  const raw = Number.isFinite(n) ? String(Math.trunc(n)) : String(value).split('.')[0];
  return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function parseNumber(value) {
  const integerPart = String(value || '').replace(/,/g, '').split('.')[0];
  const digits = integerPart.replace(/[^\d]/g, '');
  return digits === '' ? null : Number(digits);
}

function Field({ label, value, onChange, type = 'text', options, textarea }) {
  const isFormattedNumber = type === 'amount' || type === 'integer' || type === 'number';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={labelStyle}>{label}</span>
      {options ? (
        <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} style={inputStyle}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : textarea ? (
        <ExpandableTextarea
          value={value}
          onChange={onChange}
          rows={2}
          expandedRows={6}
          style={{ ...inputStyle, minHeight: 58, resize: 'vertical' }}
        />
      ) : (
        <input
          type={isFormattedNumber ? 'text' : type}
          inputMode={isFormattedNumber ? 'numeric' : undefined}
          pattern={isFormattedNumber ? '[0-9,]*' : undefined}
          value={isFormattedNumber ? numberValue(value) : (value ?? '')}
          onFocus={(e) => {
            if (isFormattedNumber) e.target.select();
          }}
          onChange={(e) => {
            const v = e.target.value;
            if (isFormattedNumber) onChange(parseNumber(v));
            else onChange(v || null);
          }}
          style={inputStyle} />
      )}
    </div>
  );
}

export default function MasterEditModal({ master, onClose, onSaved }) {
  const navigate = useNavigate();
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  // travel_code 는 수정 불가 (URL/FK key)
  const [form, setForm] = useState({
    company_code:     master.company_code,
    reservation_date: master.reservation_date,
    customer_name:    master.customer_name,
    customer_phone:   master.customer_phone,
    customer_email:   master.customer_email,
    travel_start:     master.travel_start,
    travel_end:       master.travel_end,
    adult_count:      master.adult_count,
    child_count:      master.child_count,
    infant_count:     master.infant_count,
    status:           master.status,
    source:           master.source,
    notes:            master.notes,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  async function handleSave() {
    if (!form.customer_name) { alert('예약자명 필수'); return; }
    setSaving(true);
    try {
      const r = await apiClient.put(`/api/admin/reservations/${encodeURIComponent(master.travel_code)}`, form);
      if (r.ok && r.data?.success) { onSaved && onSaved(); onClose(); }
      else alert(r.data?.message || '저장 실패');
    } catch (e) { alert('오류: ' + e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    const code = master.travel_code;
    const ok = window.confirm(`${code} 예약 전체를 삭제할까요?\n항공/렌트/호텔/결제/정산/수익 이벤트도 함께 삭제됩니다.`);
    if (!ok) return;
    const typed = window.prompt(`삭제 확인을 위해 여행코드 ${code} 를 입력하세요.`);
    if (typed !== code) {
      alert('여행코드가 일치하지 않아 삭제하지 않았습니다.');
      return;
    }
    setDeleting(true);
    try {
      const r = await apiClient.delete(`/api/admin/reservations/${encodeURIComponent(code)}`);
      if (r.ok && r.data?.success) {
        alert('예약이 삭제되었습니다.');
        onClose();
        navigate('/admin/reservations');
      } else {
        alert(r.data?.message || '삭제 실패');
      }
    } catch (e) {
      alert('삭제 오류: ' + e.message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div style={overlay} {...overlayCloseProps}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={stdModalHeaderBar(8)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>✏️</div>
            <div style={{ minWidth: 0 }}>
              <div style={stdModalHeaderTitle}>마스터 수정</div>
              <div style={stdModalHeaderSub}>{master.travel_code}</div>
            </div>
          </div>
          <button onClick={onClose} style={stdModalCloseBtn}>✕</button>
        </div>
        <div style={body}>
          <div style={grid}>
            <Field label="예약일" type="date" value={form.reservation_date} onChange={upd('reservation_date')} />
            <Field label="상태" value={form.status} onChange={upd('status')} options={STATUS_OPTS} />
            <Field label="고객명" value={form.customer_name} onChange={upd('customer_name')} />
            <Field label="연락처" value={form.customer_phone} onChange={upd('customer_phone')} />
            <Field label="이메일" value={form.customer_email} onChange={upd('customer_email')} />
            <Field label="출발" type="date" value={form.travel_start} onChange={upd('travel_start')} />
            <Field label="귀국" type="date" value={form.travel_end} onChange={upd('travel_end')} />
            <Field label="성인" type="integer" value={form.adult_count} onChange={upd('adult_count')} />
            <Field label="소아" type="integer" value={form.child_count} onChange={upd('child_count')} />
            <Field label="유아" type="integer" value={form.infant_count} onChange={upd('infant_count')} />
            <Field label="예약 경로" value={form.source} onChange={upd('source')} />
          </div>
          <Field label="비고" textarea value={form.notes} onChange={upd('notes')} />
        </div>
        <div style={footer}>
          <button onClick={handleDelete} disabled={saving || deleting} style={deleteBtn}>{deleting ? '삭제 중...' : '예약 전체 삭제'}</button>
          <div style={footerActions}>
            <button onClick={onClose} disabled={deleting} style={cancelBtn}>취소</button>
            <button onClick={handleSave} disabled={saving || deleting} style={saveBtn}>{saving ? '저장 중...' : '저장'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' };
const modal = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, width: 'min(720px, calc(100vw - 24px))', maxWidth: 'calc(100vw - 24px)', maxHeight: 'min(90vh, calc(100dvh - 24px))', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' };
const body = { padding: 16, overflowY: 'auto', display: 'grid', gap: 12 };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 };
const footer = { padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 8 };
const footerActions = { display: 'flex', gap: 8 };
const cancelBtn = { padding: '6px 14px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer' };
const saveBtn = { padding: '6px 14px', background: 'var(--accent-700, #7c3aed)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 };
const deleteBtn = { padding: '6px 14px', border: '1px solid #dc2626', borderRadius: 4, background: 'rgba(220,38,38,0.08)', color: '#dc2626', cursor: 'pointer', fontWeight: 700 };
